import React, { useState } from 'react';
import { Smartphone, Tablet, Monitor } from 'lucide-react';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface EditorSection {
  id: string;
  type: 'listings-videos' | 'custom-links';
  title: string;
  description: string;
  layout: string;
  cards: EditorCard[];
  collapsed: boolean;
}
interface DevicePreviewProps {
  sections: EditorSection[];
  socialLinks: Array<{
    enabled: boolean;
    icon: any;
  }>;
}
type DeviceType = 'mobile' | 'tablet' | 'desktop';
export function DevicePreview({
  sections,
  socialLinks
}: DevicePreviewProps) {
  const [device, setDevice] = useState<DeviceType>('mobile');
  const renderSectionPreview = (section: EditorSection) => {
    const enabledCards = section.cards.filter(c => c.enabled);
    if (enabledCards.length === 0) return null;
    if (section.type === 'listings-videos') {
      if (section.layout === 'carousel') {
        return <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2" data-id="element-66">
            {enabledCards.map(card => <div key={card.id} className="shrink-0 w-32 bg-white rounded-lg border border-brand-light p-2" data-id="element-67">
                {card.image && <div className="w-full h-20 rounded overflow-hidden mb-1 bg-brand-light" data-id="element-68">
                    <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-69" />
                  </div>}
                <p className="text-[10px] font-medium text-brand-dark truncate" data-id="element-70">
                  {card.title}
                </p>
                {card.price && <p className="text-[9px] font-mono text-brand-muted" data-id="element-71">
                    {card.price}
                  </p>}
              </div>)}
          </div>;
      }
      if (section.layout === 'small-card') {
        return <div className="grid grid-cols-2 gap-2" data-id="element-72">
            {enabledCards.map(card => <div key={card.id} className="bg-white rounded-lg border border-brand-light p-2" data-id="element-73">
                {card.image && <div className="w-full h-16 rounded overflow-hidden mb-1 bg-brand-light" data-id="element-74">
                    <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-75" />
                  </div>}
                <p className="text-[10px] font-medium text-brand-dark truncate" data-id="element-76">
                  {card.title}
                </p>
                {card.price && <p className="text-[9px] font-mono text-brand-muted" data-id="element-77">
                    {card.price}
                  </p>}
              </div>)}
          </div>;
      }
      return <div className="space-y-2" data-id="element-78">
          {enabledCards.map(card => <div key={card.id} className="bg-white rounded-lg border border-brand-light p-3 flex gap-2" data-id="element-79">
              {card.image && <div className="w-16 h-16 rounded overflow-hidden shrink-0 bg-brand-light" data-id="element-80">
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-81" />
                </div>}
              <div className="flex-1 min-w-0" data-id="element-82">
                <p className="text-xs font-medium text-brand-dark truncate" data-id="element-83">
                  {card.title}
                </p>
                <p className="text-[10px] text-brand-muted truncate" data-id="element-84">
                  {card.description}
                </p>
                {card.price && <p className="text-[10px] font-mono text-brand-dark mt-1" data-id="element-85">
                    {card.price}
                  </p>}
              </div>
            </div>)}
        </div>;
    }
    if (section.layout === 'grid') {
      return <div className="grid grid-cols-2 gap-2" data-id="element-86">
          {enabledCards.map(card => <div key={card.id} className="py-2 px-3 bg-brand-offwhite border border-brand-light rounded-lg text-center text-[10px] font-medium text-brand-dark" data-id="element-87">
              {card.title}
            </div>)}
        </div>;
    }
    return <div className="space-y-2" data-id="element-88">
        {enabledCards.map(card => <div key={card.id} className="w-full py-2 px-3 bg-brand-offwhite border border-brand-light rounded-lg text-center text-[10px] font-medium text-brand-dark" data-id="element-89">
            {card.title}
          </div>)}
      </div>;
  };
  const getFrameStyles = () => {
    if (device === 'mobile') {
      return 'w-[320px] h-[650px] border-[10px] border-gray-900 rounded-[2.5rem]';
    }
    if (device === 'tablet') {
      return 'w-[768px] h-[600px] border-4 border-gray-800 rounded-2xl';
    }
    return 'w-full h-[600px] border-2 border-gray-300 rounded-xl';
  };
  return <div className="hidden lg:flex w-[900px] bg-white border-l border-brand-light flex-col items-center py-8 sticky top-0 h-screen overflow-hidden" data-id="element-90">
      <div className="mb-4 flex items-center gap-2 bg-brand-offwhite rounded-full p-1 border border-brand-light" data-id="element-91">
        <button onClick={() => setDevice('mobile')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'mobile' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-92">
          <Smartphone size={14} data-id="element-93" />
          Mobile
        </button>
        <button onClick={() => setDevice('tablet')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'tablet' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-94">
          <Tablet size={14} data-id="element-95" />
          Tablet
        </button>
        <button onClick={() => setDevice('desktop')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === 'desktop' ? 'bg-brand-forest text-white' : 'text-brand-muted hover:text-brand-dark'}`} data-id="element-96">
          <Monitor size={14} data-id="element-97" />
          Desktop
        </button>
      </div>

      <div className="mb-4 px-4 py-2 bg-brand-offwhite rounded-full border border-brand-light text-xs font-mono text-brand-muted flex items-center gap-2" data-id="element-98">
        <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-99"></span>
        eaves.pro/marcuschen
      </div>

      <div className={`${getFrameStyles()} overflow-hidden shadow-2xl relative bg-brand-offwhite`} data-id="element-100">
        {device === 'mobile' && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-50" data-id="element-101"></div>}

        {device === 'desktop' && <div className="absolute top-0 left-0 right-0 h-8 bg-gray-100 border-b border-gray-300 flex items-center px-3 gap-1.5" data-id="element-102">
            <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-103"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-400" data-id="element-104"></div>
            <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-105"></div>
            <div className="ml-2 flex-1 bg-white rounded px-2 py-0.5 text-[9px] text-brand-muted font-mono" data-id="element-106">
              eaves.pro/marcuschen
            </div>
          </div>}

        <div className={`h-full overflow-y-auto hide-scrollbar ${device === 'desktop' ? 'pt-8' : ''}`} data-id="element-107">
          <div className="h-24 bg-gradient-to-r from-brand-sage via-brand-sandstoneBg to-brand-light relative" data-id="element-108">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-forest to-transparent" data-id="element-109"></div>
          </div>

          <div className="px-4 -mt-12 relative z-10" data-id="element-110">
            <div className="bg-white rounded-xl shadow-sm border border-brand-light p-4" data-id="element-111">
              <div className="w-20 h-20 rounded-full border-4 border-white shadow-sm bg-white -mt-10 mb-3 mx-auto overflow-hidden" data-id="element-112">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Marcus Chen" className="w-full h-full object-cover" data-id="element-113" />
              </div>

              <div className="text-center mb-4" data-id="element-114">
                <h1 className="text-base font-serif font-semibold text-brand-dark mb-0.5" data-id="element-115">
                  Marcus Chen
                </h1>
                <h2 className="text-[10px] text-brand-muted font-serif italic mb-2" data-id="element-116">
                  Senior real estate advisor
                </h2>
              </div>

              <div className="flex justify-center gap-1.5 mb-4" data-id="element-117">
                {socialLinks.filter(l => l.enabled).map((link, i) => {
                const Icon = link.icon;
                return <div key={i} className="w-7 h-7 rounded-full bg-brand-offwhite flex items-center justify-center" data-id="element-118">
                        <Icon size={12} className="text-brand-muted" data-id="element-119" />
                      </div>;
              })}
              </div>
            </div>

            <div className="mt-4 space-y-4 pb-8" data-id="element-120">
              {sections.map(section => <div key={section.id} className="bg-white rounded-xl border border-brand-light p-3" data-id="element-121">
                  <h3 className="text-xs font-serif font-medium text-brand-dark mb-1" data-id="element-122">
                    {section.title}
                  </h3>
                  {section.description && <p className="text-[9px] text-brand-muted mb-2" data-id="element-123">
                      {section.description}
                    </p>}
                  {renderSectionPreview(section)}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}